/**
 * Represents a class for managing accessibility settings.
 *
 * @class
 */
class WCAG {

	buttons = [];

	buttonNormalText = null;

	buttonClass = 'js-accessibility-menu-button';

	buttonClassActive = 'accessibility-menu__button--active';

	/**
	 * Initializes a new instance of the Constructor class.
	 *
	 * @class
	 * @memberof Namespace
	 */
	constructor() {

		this.selectElements();
		this.initVariables();

		if ( !this.header ) {

			return;

		}
		if ( this.buttons.length ) {

			this.event()

		}
		this.initializeFunctionalities();

	}

	/**
	 * Selects necessary DOM elements and assigns them to class properties.
	 *
	 * @return {void}
	 */
	selectElements() {

		this.header = document.querySelector( 'header' );
		this.buttons = document.querySelectorAll( `.${this.buttonClass}` );
		this.buttonNormalText = document.querySelector( `.${this.buttonClass}[data-type='normal-text']` );

	}

	/**
	 * Initializes variables
	 *
	 * @return {void}
	 */
	initVariables() {

		this.easeOfReadingMouseMoveHandler = null;
		this.rulerElement = null;

	}

	/**
	 * Attaches event listeners to buttons and open menu button.
	 *
	 * @function event
	 */
	event() {

		this.buttons.forEach( ( button ) => {

			button.addEventListener( 'click', () => this.handleButton( button ) );

		} );

	}

	/**
	 * Checks if a button is currently active.
	 *
	 * @param {HTMLElement} button - The button element to check.
	 * @return {boolean} - True if the button is active, false otherwise.
	 */
	buttonIsActive( button ) {

		return button.classList.contains( this.buttonClassActive );

	}

	/**
	 * Handles the logic for a button click event.
	 *
	 * @param {HTMLElement} button - The button element that was clicked.
	 */
	handleButton( button ) {

		const type = button.dataset.type;

		const screenTypes = ['contrast', 'grayscale'];
		const fontsTypes = ['normal-text', 'larger-text', 'largest-text'];

		if ( screenTypes.includes( type ) ) {

			if ( this.buttonIsActive( button ) ) {

				this.setButtonDeactive( button )

			} else {

				screenTypes.forEach( ( screenType ) => {

					if ( screenType === type ) {

						this.setButtonActive( button )

					} else {

						this.buttons.forEach( ( btn ) => {

							if ( btn.dataset.type === screenType ) {

								this.setButtonDeactive( btn )

							}

						} )

					}

				} )

			}

		} else if ( fontsTypes.includes( type ) ) {

			if ( this.buttonIsActive( button ) && button !== this.buttonNormalText ) {

				this.setButtonDeactive( button )
				this.setButtonActive( this.buttonNormalText )


			} else {

				fontsTypes.forEach( ( screenType ) => {


					if ( screenType === type ) {

						this.setButtonActive( button )

					} else {

						this.buttons.forEach( ( btn ) => {

							if ( btn.dataset.type === screenType ) {

								this.setButtonDeactive( btn )

							}

						} )

					}

				} )

			}

		} else if ( this.buttonIsActive( button ) ) {

			this.setButtonDeactive( button )

		} else {

			this.setButtonActive( button )

		}

	}


	/**
	 * Activates the functionality associated with the button.
	 *
	 * @param {HTMLElement} button - The button element to be set as active.
	 * @param {boolean} updateCookie - Whether to update the cookie.
	 */
	setButtonActive( button, updateCookie = true ) {

		button.classList.add( this.buttonClassActive );
		document.documentElement.classList.add( `accessibility--${button.dataset.type}` );

		// Call the functionality enableEaseOfReading if type is ease-of-reading
		if( button.dataset.type  === 'ease-of-reading' ) {

			this.enableEaseOfReading();

		}

		if ( updateCookie ) {

			this.setCookie( button.dataset.type, 'true' );

		}

	}

	/**
	 * Deactivates the functionality associated with the button.
	 *
	 * @param {HTMLElement} button - The button element to be set as inactive.
	 * @param {boolean} updateCookie - Whether to update the cookie.
	 */
	setButtonDeactive( button, updateCookie = true ) {

		button.classList.remove( this.buttonClassActive );
		document.documentElement.classList.remove( `accessibility--${button.dataset.type}` );

		// Call the functionality disableEaseOfReading if type is ease-of-reading
		if( button.dataset.type  === 'ease-of-reading' ) {

			this.disableEaseOfReading();

		}

		if ( updateCookie ) {

			this.setCookie( button.dataset.type, 'false' );

		}

	}

	/**
	 * Sets the value of a cookie.
	 *
	 * @param {string} cookieKey - The key of the cookie.
	 * @param {string} cookieValue - The value to be set for the cookie.
	 */
	setCookie( cookieKey, cookieValue ) {

		const accessibility = this.getCookie();

		accessibility[cookieKey] = cookieValue;
		document.cookie = `accessibility=${JSON.stringify( accessibility )}; path=/;`;

	}


	/**
	 * Returns the value of the "accessibility" cookie.
	 *
	 * @return {Object} The value of the "accessibility" cookie, parsed as JSON.
	 */
	getCookie() {

		const allCookies = document.cookie.split( '; ' );

		for ( let i = 0; i < allCookies.length; i++ ) {

			const cookiePair = allCookies[i].split( '=' );
			if ( cookiePair[0] === 'accessibility' ) {

				return JSON.parse( cookiePair[1] );

			}

		}

		return {};

	}


	/**
	 * Initializes functionalities based on the cookies.
	 */
	initializeFunctionalities() {

		const accessibility = this.getCookie();

		Object.keys( accessibility ).forEach( ( type ) => {

			if ( accessibility[type] === 'true' ) {

				const button = Array.from( this.buttons ).find(
					( btn ) => btn.dataset.type === type
				);
				if ( button ) {

					this.setButtonActive( button, false );

				}

			}

		} );

		const fontsTypes = ['normal-text', 'larger-text', 'largest-text'];
		const fontSizeSet = fontsTypes.some( ( fontType ) => accessibility[fontType] === 'true' );

		// If no text size is set, activate 'normal-text' by default.
		if ( !fontSizeSet ) {

			this.setButtonActive( this.buttonNormalText, false );

		}

	}

	/**
	 * Enables the "ease of reading" functionality.
	 */
	enableEaseOfReading() {

		// Create the ruler element
		this.rulerElement = document.createElement( 'div' );
		this.rulerElement.id = 'wcag-ruler';

		document.body.appendChild( this.rulerElement );

		// Define and store the mousemove handler
		this.easeOfReadingMouseMoveHandler = ( event ) => {

			const y = event.clientY;
			this.rulerElement.style.top = `${y}px`;

		};

		// Add the mousemove event listener
		document.addEventListener( 'mousemove', this.easeOfReadingMouseMoveHandler );

	}


	/**
	 * Disables the "ease of reading" functionality.
	 */
	disableEaseOfReading() {

		// Remove the ruler element if it exists
		if ( this.rulerElement ) {

			this.rulerElement.remove();
			this.rulerElement = null;

		}

		// Remove the mousemove event listener
		if ( this.easeOfReadingMouseMoveHandler ) {

			document.removeEventListener( 'mousemove', this.easeOfReadingMouseMoveHandler );
			this.easeOfReadingMouseMoveHandler = null;

		}

	}

}

export default WCAG;
